import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFileAudio, FaFileImage, FaFileVideo } from 'react-icons/fa';
import { FiUpload } from 'react-icons/fi';

import FileDropzone from '@/components/FileDropzone/FileDropzone.tsx';
import { SongMedia } from '@/core/db.ts';

interface SongFileDropProps {
  supports: 'image' | 'video' | 'audio' | 'media';
  onFileAdd: (type: SongMedia['type'], file: ArrayBuffer) => void;
  pending?: boolean;
}

const onHandleImage = (file: File) => {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return reject(new Error('No canvas context found.'));
      }

      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      ctx.drawImage(image, 0, 0);

      canvas.toBlob(async (blob) => {
        if (!blob) {
          return reject(new Error('Could not convert to blob.'));
        }

        const buffer = await blob.arrayBuffer();

        resolve(buffer);
      }, 'image/webp');
    };

    image.src = URL.createObjectURL(file);
  });
}

const onHandleFile = async (file: File) => file.arrayBuffer();

function SongFileDrop({
  supports,
  pending = false,
  onFileAdd,
}: SongFileDropProps) {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      return;
    }

    setIsBusy(true);

    const file = acceptedFiles[0];
    const type = file.type.split('/')[0];

    switch (type) {
      case 'image':
        onFileAdd(type, await onHandleImage(acceptedFiles[0]));
        break;
      case 'audio':
      case 'video':
        onFileAdd(type, await onHandleFile(acceptedFiles[0]));
        break;
    }

    setIsBusy(false);
  }, [supports, onFileAdd]);

  const Icon = useMemo(() => {
    switch (supports) {
      case 'media':
        return FiUpload;
      case 'video':
        return FaFileVideo;
      case 'audio':
        return FaFileAudio;
      case 'image':
      default:
        return FaFileImage;
    }
  }, [supports]);

  const accept = useMemo(() => {
    switch (supports) {
      case 'media':
        return ['video/*', 'audio/*', 'image/*'];
      case 'video':
        return 'video/*';
      case 'audio':
        return 'audio/*';
      case 'image':
      default:
        return 'image/*';
    }
  }, [supports]);

  const supportText = useMemo(() => {
    switch (supports) {
      case 'media':
        return {
          title: t('media.file.title.media'),
          supports: t('media.file.supports.media'),
        };
      case 'video':
        return {
          title: t('media.file.title.video'),
          supports: t('media.file.supports.video'),
        };
      case 'audio':
        return {
          title: t('media.file.title.audio'),
          supports: t('media.file.supports.audio'),
        };
      case 'image':
      default:
        return {
          title: t('media.file.title.image'),
          supports: t('media.file.supports.image'),
        };
    }
  }, [supports, t]);

  return (
    <FileDropzone
      Icon={Icon}
      accept={accept}
      isBusy={isBusy || pending}
      text={{
        title: t('media.file.dragTitle', { title: supportText.title }),
        supports: supportText.supports,
      }}
      onDrop={onDrop}
    />
  );
}

export default SongFileDrop;
